import { List } from 'immutable';
import { IDispatchId, IRequestError } from '../../utils/helpers/redux.helper';
import { Notification } from './notification.model';

// action enums
export enum ActionsEnum {
  FETCH_NOTIFICATIONS = 'FETCH_NOTIFICATIONS',
  FETCH_NOTIFICATIONS_SUCCESS = 'FETCH_NOTIFICATIONS_SUCCESS',
  FETCH_NOTIFICATIONS_ERROR = 'FETCH_NOTIFICATIONS_ERROR',
  UPDATE_NOTIFICATIONS = 'UPDATE_NOTIFICATIONS',
  BATCH_MARK_NOTIFICATIONS_AS_SEEN = 'BATCH_MARK_NOTIFICATIONS_AS_SEEN',
  BATCH_MARK_NOTIFICATIONS_AS_SEEN_SUCCESS = 'BATCH_MARK_NOTIFICATIONS_AS_SEEN_SUCCESS',
  BATCH_MARK_NOTIFICATIONS_AS_SEEN_ERROR = 'BATCH_MARK_NOTIFICATIONS_AS_SEEN_ERROR',
  MARK_NOTIFICATION_AS_READ = 'MARK_NOTIFICATION_AS_READ',
  MARK_NOTIFICATION_AS_READ_SUCCESS = 'MARK_NOTIFICATION_AS_READ_SUCCESS',
  MARK_NOTIFICATION_AS_READ_ERROR = 'MARK_NOTIFICATION_AS_READ_ERROR',
  DISMISS_NOTIFICATION = 'DISMISS_NOTIFICATION',
  DISMISS_NOTIFICATION_SUCCESS = 'DISMISS_NOTIFICATION_SUCCESS',
  DISMISS_NOTIFICATION_ERROR = 'DISMISS_NOTIFICATION_ERROR',
  REGISTER_NOTIFICATIONS_POLLING = 'REGISTER_NOTIFICATIONS_POLLING',
  UNREGISTER_NOTIFICATIONS_POLLING = 'UNREGISTER_NOTIFICATIONS_POLLING',
}

// action payloads
export interface IBatchNotificationsPayload {
  notificationIds: List<string>;
}

export interface INotificationPayload {
  notification: Notification;
}

export interface INotificationsPayload {
  notifications: List<Notification>;
}

export interface ISingleNotificationRequest
  extends IDispatchId,
    INotificationPayload {}

export interface IBatchNotificationsRequest
  extends IDispatchId,
    IBatchNotificationsPayload {}

export interface ISingleNotificationError
  extends IRequestError,
    INotificationPayload {}

export interface IBatchNotificationsError
  extends IRequestError,
    IBatchNotificationsPayload {}

export interface IRegisterNotificationsPolling {
  interval: number;
}

// action definitions
export interface FetchNotifications {
  type: ActionsEnum.FETCH_NOTIFICATIONS;
  payload: IDispatchId;
}

export interface FetchNotificationsSuccess {
  type: ActionsEnum.FETCH_NOTIFICATIONS_SUCCESS;
  payload: IDispatchId;
}

export interface FetchNotificationsError {
  type: ActionsEnum.FETCH_NOTIFICATIONS_ERROR;
  payload: IRequestError;
}

export interface UpdateNotifications {
  type: ActionsEnum.UPDATE_NOTIFICATIONS;
  payload: INotificationsPayload;
}

export interface BatchMarkNotificationsAsSeen {
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN;
  payload: IBatchNotificationsRequest;
}

export interface BatchMarkNotificationsAsSeenSuccess {
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN_SUCCESS;
  payload: IDispatchId;
}

export interface BatchMarkNotificationsAsSeenError {
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN_ERROR;
  payload: IBatchNotificationsError;
}

export interface MarkNotificationAsRead {
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ;
  payload: ISingleNotificationRequest;
}

export interface MarkNotificationAsReadSuccess {
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ_SUCCESS;
  payload: any;
}

export interface MarkNotificationAsReadError {
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ_ERROR;
  payload: ISingleNotificationError;
}

export interface DismissNotification {
  type: ActionsEnum.DISMISS_NOTIFICATION;
  payload: ISingleNotificationRequest;
}

export interface DismissNotificationSuccess {
  type: ActionsEnum.DISMISS_NOTIFICATION_SUCCESS;
  payload: any;
}

export interface DismissNotificationError {
  type: ActionsEnum.DISMISS_NOTIFICATION_ERROR;
  payload: ISingleNotificationError;
}

export interface RegisterNotificationsPolling {
  type: ActionsEnum.REGISTER_NOTIFICATIONS_POLLING;
  payload: IRegisterNotificationsPolling;
}

export interface UnregisterNotificationsPolling {
  type: ActionsEnum.UNREGISTER_NOTIFICATIONS_POLLING;
}

// action creators
// TODO: handle pagination
export const fetchNotifications = (
  dispatchId: string,
  page?: number
): FetchNotifications => ({
  type: ActionsEnum.FETCH_NOTIFICATIONS,
  payload: {
    dispatchId,
  },
});

export const fetchNotificationsSuccess = (
  dispatchId: string
): FetchNotificationsSuccess => ({
  type: ActionsEnum.FETCH_NOTIFICATIONS_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const fetchNotificationsError = (
  dispatchId: string,
  error: Error
): FetchNotificationsError => ({
  type: ActionsEnum.FETCH_NOTIFICATIONS_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const updateNotifications = (
  notifications: List<Notification>
): UpdateNotifications => ({
  type: ActionsEnum.UPDATE_NOTIFICATIONS,
  payload: {
    notifications,
  },
});

export const batchMarkNotificationsAsSeen = (
  dispatchId: string,
  notificationIds: List<string>
): BatchMarkNotificationsAsSeen => ({
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN,
  payload: {
    dispatchId,
    notificationIds,
  },
});

export const batchMarkNotificationsAsSeenSuccess = (
  dispatchId: string
): BatchMarkNotificationsAsSeenSuccess => ({
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const batchMarkNotificationsAsSeenError = (
  dispatchId: string,
  notificationIds: List<string>,
  error: Error
): BatchMarkNotificationsAsSeenError => ({
  type: ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN_ERROR,
  payload: {
    dispatchId,
    notificationIds,
    error,
  },
});

export const markNotificationAsRead = (
  dispatchId: string,
  notification: Notification
): MarkNotificationAsRead => ({
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ,
  payload: {
    dispatchId,
    notification,
  },
});

export const markNotificationAsReadSuccess = (
  dispatchId: string
): MarkNotificationAsReadSuccess => ({
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const markNotificationAsReadError = (
  dispatchId: string,
  notification: Notification,
  error: Error
): MarkNotificationAsReadError => ({
  type: ActionsEnum.MARK_NOTIFICATION_AS_READ_ERROR,
  payload: {
    dispatchId,
    notification,
    error,
  },
});

export const dismissNotification = (
  dispatchId: string,
  notification: Notification
): DismissNotification => ({
  type: ActionsEnum.DISMISS_NOTIFICATION,
  payload: {
    dispatchId,
    notification,
  },
});

export const dismissNotificationSuccess = (
  dispatchId: string
): DismissNotificationSuccess => ({
  type: ActionsEnum.DISMISS_NOTIFICATION_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const dismissNotificationError = (
  dispatchId: string,
  notification: Notification,
  error: Error
): DismissNotificationError => ({
  type: ActionsEnum.DISMISS_NOTIFICATION_ERROR,
  payload: {
    dispatchId,
    notification,
    error,
  },
});

export const registerNotificationsPolling = (
  interval: number
): RegisterNotificationsPolling => ({
  type: ActionsEnum.REGISTER_NOTIFICATIONS_POLLING,
  payload: {
    interval,
  },
});

export const unregisterNotificationsPolling = (): UnregisterNotificationsPolling => ({
  type: ActionsEnum.UNREGISTER_NOTIFICATIONS_POLLING,
});
